import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import DropdownItem from "react-bootstrap/DropdownItem";
import Table from "@enerex/unified-ui-react/Table";
import Select from "components/UnifiedSelect";
import CreatableSelect from "react-select/creatable";
import FormInput from "components/FormInput/index";
import FileUploader from "components/UnifiedFileUploader";
import Div from "../../../components/Div";
import PageTitle from "../../../components/PageTitle/index";
import ThreeDotMenu from "../../../components/ThreeDotMenu";
import PlusIcon from '@enerex/unified-ui-react/icons/AddIcon';
import { DEV_URL, rdelete, rget, rpost, rput } from "../../../utils/requests";
import useShowSnackbar from "hooks/useShowSnackbar";
import { useAuth } from "providers/CustomAuthProvider";
import { buildCompanyLogoUrl } from "../../../utils/logo";

const COMPANY_PAGE_SIZE = 30;
const BRAND_SEARCH_PAGE_SIZE = 20;
const DEFAULT_STATUS = "Active";
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];
const DEFAULT_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];
const LOGO_FILE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";
const LOGO_FILE_ERROR = "Please select a JPG, PNG, GIF, or WEBP image.";
const LOGO_PREVIEW_DEBOUNCE_MS = 500;
const BRAND_SEARCH_DEBOUNCE_MS = 400;

const findOptionByValue = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const websiteLogoPreviewContainerStyle = {
  width: 42,
  height: 36,
  minWidth: 42,
  border: "1px solid var(--ct-border-color)",
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "var(--ct-tertiary-bg, #f8f9fa)",
};

const websiteLogoPreviewImageStyle = {
  width: 28,
  height: 28,
  objectFit: "contain",
};

const WebsiteLogoPreview = ({ logoUrl, fallbackLogoUrl }) => {
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const previewUrl = fallbackLogoUrl || (logoLoadFailed ? "" : logoUrl);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [logoUrl]);

  return (
    <div
      style={websiteLogoPreviewContainerStyle}
      aria-label="Website logo preview"
    >
      {previewUrl ? (
        <img
          key={previewUrl}
          src={previewUrl}
          alt="Website logo preview"
          style={websiteLogoPreviewImageStyle}
          onLoad={(e) => {
            e.currentTarget.style.display = "block";
          }}
          onError={(e) => {
            if (previewUrl === logoUrl) {
              setLogoLoadFailed(true);
            }
          }}
        />
      ) : (
        <span className="text-muted" style={{ fontSize: 10 }}>
          Logo
        </span>
      )}
    </div>
  );
};

const brandRowLogoImageStyle = {
  width: 32,
  height: 32,
  objectFit: "contain",
  border: "1px solid var(--ct-border-color)",
  borderRadius: 4,
  padding: 3,
};

const BrandRowLogo = ({ website, logo, brandName }) => {
  const [websiteLogoFailed, setWebsiteLogoFailed] = useState(false);
  const [fallbackLogoFailed, setFallbackLogoFailed] = useState(false);
  const websiteLogoUrl = buildCompanyLogoUrl(website);
  const logoUrl =
    logo && !fallbackLogoFailed
      ? logo
      : websiteLogoUrl && !websiteLogoFailed
        ? websiteLogoUrl
        : "";

  useEffect(() => {
    setWebsiteLogoFailed(false);
  }, [websiteLogoUrl]);

  useEffect(() => {
    setFallbackLogoFailed(false);
  }, [logo]);

  if (!logoUrl) {
    return <span className="text-muted">-</span>;
  }

  return (
    <img
      key={logoUrl}
      src={logoUrl}
      alt={`${brandName || "Brand"} logo`}
      style={brandRowLogoImageStyle}
      onError={() => {
        if (logoUrl === logo) {
          setFallbackLogoFailed(true);
          return;
        }

        setWebsiteLogoFailed(true);
      }}
    />
  );
};

const BrandStatusIndicator = ({ status }) => {
  const active = normalizeStatusValue(status) === "Active";
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: active ? "#198754" : "#dc3545",
          display: "inline-block",
        }}
      />
      <span>{getStatusLabel(status)}</span>
    </span>
  );
};

const companySelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "33px",
    borderColor: "var(--ct-input-border-color)",
    "&:hover": { borderColor: "var(--ct-input-border-color)" },
  }),
  menu: (base) => ({ ...base, marginTop: 0, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const fetchCompanyPage = async (searchTerm, pageNumber) => {
  const res = await rpost(`${DEV_URL}/api/company/getall`, {
    PageNumber: pageNumber,
    PageSize: COMPANY_PAGE_SIZE,
    SearchTerm: searchTerm || null,
    CompanyID: null,
    ClientID: null,
    LicenseID: null,
    orderBy: null,
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    LicenseStatus: null,
  });

  const json = res?.data;
  let items = [];
  if (json?.company && Array.isArray(json.company)) {
    items = json.company;
  } else if (json?.Company && Array.isArray(json.Company)) {
    items = json.Company;
  } else if (json?.data?.company && Array.isArray(json.data.company)) {
    items = json.data.company;
  } else if (json?.data?.Company && Array.isArray(json.data.Company)) {
    items = json.data.Company;
  } else if (Array.isArray(json)) {
    items = json;
  } else if (json?.data && Array.isArray(json.data)) {
    items = json.data;
  }

  return items
    .map((c) => ({
      value: c.id ?? c.Id,
      label: c.legalName || c.LegalName || c.name || c.companyName || "",
    }))
    .filter((option) => option.value && option.label);
};

const getErrorMessage = (response, fallback) =>
  response?.data?.message ||
  response?.data?.Message ||
  response?.data?.error ||
  fallback;

const getWebsiteUrl = (website) => {
  const value = (website || "").trim();
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const normalizeStatusValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return DEFAULT_STATUS;
  }

  const normalized = String(value).trim().toLowerCase();
  return ["0", "false", "inactive"].includes(normalized) ? "Inactive" : "Active";
};

const getStatusLabel = (value) =>
  normalizeStatusValue(value);

const normalizeText = (value) => (value || "").trim().toLowerCase();

const isValidDomainOnly = (website) => {
  const value = (website || "").trim();
  if (!value) return true;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return false;
  if (/[\/\\\s@]/.test(value)) return false;
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i.test(value);
};

const getResponseConflicts = (response) =>
  response?.data?.conflicts || response?.data?.Conflicts || [];

const getResponseValidationType = (response) =>
  (response?.data?.validationType || response?.data?.ValidationType || "")
    .toString()
    .toLowerCase();

const getConflictMatchType = (conflict) =>
  (conflict?.matchType || conflict?.MatchType || "").toString().toLowerCase();

const getBrandNameConflicts = (response) =>
  getResponseConflicts(response).filter((conflict) =>
    ["brand", "brandname", "brand_name"].includes(getConflictMatchType(conflict))
  );

const hasBrandNameConflict = (response) =>
  getResponseValidationType(response) === "brandname" ||
  getBrandNameConflicts(response).length > 0;

const normalizeDomainConflict = (conflict, website) => {
  const companyId = conflict?.companyId || conflict?.CompanyId;
  if (!companyId) return null;

  return {
    id: String(companyId),
    companyId: String(companyId),
    companyName:
      conflict?.companyName ||
      conflict?.CompanyName ||
      conflict?.legalName ||
      conflict?.LegalName ||
      "Company",
    companyStatus: conflict?.companyStatus || conflict?.CompanyStatus || "",
    domain:
      conflict?.matchedValue ||
      conflict?.MatchedValue ||
      conflict?.domain ||
      conflict?.Domain ||
      website ||
      "",
  };
};

const getDuplicateDomainConflicts = (response, website) => {
  const conflicts = getResponseConflicts(response);
  const validationType = getResponseValidationType(response);
  const domainConflicts = conflicts.filter(
    (conflict) =>
      (conflict?.matchType || conflict?.MatchType || "").toString().toLowerCase() ===
      "domain"
  );

  if (validationType !== "domain" && domainConflicts.length === 0) {
    return [];
  }

  const uniqueCompanies = new Map();
  domainConflicts
    .map((conflict) => normalizeDomainConflict(conflict, website))
    .filter(Boolean)
    .forEach((item) => {
      if (!uniqueCompanies.has(item.companyId)) {
        uniqueCompanies.set(item.companyId, item);
      }
    });

  return Array.from(uniqueCompanies.values());
};

const normalizeBrandValidationConflict = (conflict) => {
  const matchType = conflict?.matchType || conflict?.MatchType || "";
  const matchedValue = conflict?.matchedValue || conflict?.MatchedValue || "";
  const domain = conflict?.domain || conflict?.Domain || "";
  const brandName =
    conflict?.brandName ||
    conflict?.BrandName ||
    (String(matchType).toLowerCase() === "brand" ? matchedValue : "");

  return {
    companyId: conflict?.companyId || conflict?.CompanyId || "",
    companyName:
      conflict?.companyName ||
      conflict?.CompanyName ||
      conflict?.legalName ||
      conflict?.LegalName ||
      "Company",
    brandName,
    domain,
    matchType,
    matchedValue,
  };
};

const renderBrandValidationMessage = (response, fallback) => {
  const brandConflicts = getBrandNameConflicts(response);
  const message =
    brandConflicts.length > 0
      ? "Brand name already exists."
      : getErrorMessage(response, fallback);
  const conflicts =
    brandConflicts.length > 0 ? brandConflicts : getResponseConflicts(response);
  const firstConflict = conflicts[0];
  const companyId = firstConflict?.companyId || firstConflict?.CompanyId;
  const companyName =
    firstConflict?.companyName || firstConflict?.CompanyName || "";

  if (!companyId) {
    return message;
  }

  const companyLink = (
    <a
      href={`/companies/${companyId}`}
      target="_blank"
      rel="noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    >
      {companyName || "company"}
    </a>
  );
  const moreText =
    conflicts.length > 1 ? ` and ${conflicts.length - 1} more` : "";

  return (
    <span>
      {message} for {companyLink}
      {moreText}
    </span>
  );
};

const buildBrandFormData = ({
  brandName,
  status,
  website,
  logo,
  companyId,
  brandId,
  isDefault,
  aliasId,
}) => {
  const formData = new FormData();
  formData.append("BrandName", brandName.trim());
  formData.append("Status", normalizeStatusValue(status));
  formData.append("Website", (website || "").trim());
  formData.append("IsDefault", isDefault ? "true" : "false");

  if (logo) {
    formData.append("Logo", logo);
  }

  if (companyId) {
    formData.append("CompanyId", companyId);
  }

  if (brandId) {
    formData.append("BrandId", brandId);
  }

  if (aliasId) {
    formData.append("AliasId", aliasId);
  }

  return formData;
};

const normalizeBrand = (item) => {
  const companyId = item.companyId || item.CompanyId || null;
  return {
    id: item.id || item.Id,
    brandName: item.brandName || item.BrandName || "",
    status: normalizeStatusValue(
      item.status ??
      item.Status ??
      item.brandCompanyStatus ??
      item.BrandCompanyStatus
    ),
    logo: item.logo || item.Logo || "",
    website: item.website || item.Website || "",
    createdAt: item.createdAt || item.CreatedAt || "",
    brandCompanyId: item.brandCompanyId || item.BrandCompanyId || null,
    companyId,
    companyName: item.companyName || item.CompanyName || "",
    isDefault:
      item.isDefault === true ||
      item.IsDefault === true ||
      item.isDefault === 1 ||
      item.IsDefault === 1,
  };
};

const fetchBrandOptions = async (searchTerm) => {
  const response = await rpost(`${DEV_URL}/api/Brand/GetAll`, {
    PageNumber: 1,
    PageSize: BRAND_SEARCH_PAGE_SIZE,
    SearchTerm: searchTerm || "",
    CompanyID: null,
    ClientID: null,
    LicenseID: null,
    orderBy: "brandName",
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    LicenseStatus: null,
    HasCompany: 0,
  });

  const json = response?.data;
  const rows = Array.isArray(json?.brands)
    ? json.brands
    : Array.isArray(json?.Brands)
      ? json.Brands
      : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.Data)
          ? json.Data
          : Array.isArray(json)
            ? json
            : [];

  return rows
    .map(normalizeBrand)
    .filter(
      (brand) =>
        brand.id &&
        brand.brandName &&
        !brand.companyId &&
        (!brand.companyName ||
          String(brand.companyName).toLowerCase() === "unassigned")
    );
};

export const BrandsData = ({
  companyId,
  prefillAlias,
  onConvertHandled,
  onRefreshAliases,
  onRefreshDomains,
}) => {
  const snackbar = useShowSnackbar();
  const snackbarRef = useRef(snackbar);
  const { isSystemAdmin } = useAuth();

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [newBrandStatus, setNewBrandStatus] = useState(DEFAULT_STATUS);
  const [newBrandWebsite, setNewBrandWebsite] = useState("");
  const [newBrandWebsiteLogoPreview, setNewBrandWebsiteLogoPreview] =
    useState("");
  const [newBrandLogo, setNewBrandLogo] = useState(null);
  const [newBrandLogoFallbackPreview, setNewBrandLogoFallbackPreview] =
    useState("");
  const [newBrandIsDefault, setNewBrandIsDefault] = useState(false);
  const [selectedAliasOption, setSelectedAliasOption] = useState(null);
  const [isAliasConversion, setIsAliasConversion] = useState(false);
  const [addingBrand, setAddingBrand] = useState(false);
  const [selectedUpsertBrand, setSelectedUpsertBrand] = useState(null);
  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchOptions, setBrandSearchOptions] = useState([]);
  const [brandSearchLoading, setBrandSearchLoading] = useState(false);
  const brandSearchTimerRef = useRef(null);

  const [editModal, setEditModal] = useState({ show: false, row: null });
  const [editBrandName, setEditBrandName] = useState("");
  const [editStatus, setEditStatus] = useState(DEFAULT_STATUS);
  const [editWebsite, setEditWebsite] = useState("");
  const [editWebsiteLogoPreview, setEditWebsiteLogoPreview] = useState("");
  const [editLogo, setEditLogo] = useState(null);
  const [editLogoFallbackPreview, setEditLogoFallbackPreview] = useState("");
  const [editIsDefault, setEditIsDefault] = useState(false);
  const [editBrandIsDefault, setEditBrandIsDefault] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, row: null });
  const [deletingBrand, setDeletingBrand] = useState(false);
  const [checkingBrandMappingId, setCheckingBrandMappingId] = useState(null);

  const [transferModal, setTransferModal] = useState({
    show: false,
    row: null,
    fromMappedRemove: false,
  });
  const [selectedTransferCompany, setSelectedTransferCompany] = useState(null);
  const [transferringBrand, setTransferringBrand] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferCompanyOptions, setTransferCompanyOptions] = useState([]);
  const [transferCompanyLoading, setTransferCompanyLoading] = useState(false);
  const transferCompanyPageRef = useRef(1);
  const transferCompanySearchRef = useRef("");
  const transferCompanyHasMoreRef = useRef(true);
  const transferCompanySearchTimerRef = useRef(null);
  const [validationConflictModal, setValidationConflictModal] = useState({
    show: false,
    conflicts: [],
  });
  const [duplicateDomainModal, setDuplicateDomainModal] = useState({
    show: false,
    website: "",
    companies: [],
    source: null,
  });
  const [selectedDuplicateCompanyIds, setSelectedDuplicateCompanyIds] =
    useState({});
  const [resolvingDuplicateDomain, setResolvingDuplicateDomain] =
    useState(false);

  useEffect(() => {
    snackbarRef.current = snackbar;
  }, [snackbar]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setNewBrandWebsiteLogoPreview(buildCompanyLogoUrl(newBrandWebsite));
    }, LOGO_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [newBrandWebsite]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setEditWebsiteLogoPreview(buildCompanyLogoUrl(editWebsite));
    }, LOGO_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [editWebsite]);

  useEffect(() => {
    if (!newBrandLogo) {
      setNewBrandLogoFallbackPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(newBrandLogo);
    setNewBrandLogoFallbackPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [newBrandLogo]);

  useEffect(() => {
    if (!editLogo) {
      setEditLogoFallbackPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(editLogo);
    setEditLogoFallbackPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [editLogo]);

  const fetchBrands = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await rpost(`${DEV_URL}/api/Brand/GetAll`, {
        PageNumber: 1,
        PageSize: 100,
        SearchTerm: "",
        CompanyID: companyId,
        ClientID: null,
        LicenseID: null,
        orderBy: "brandName",
        StateCode: null,
        Commodity: null,
        LicenseType: null,
        ascendingOrder: true,
        LicenseStatus: null,
        HasCompany: 1,
      });

      if (!response || response.status < 200 || response.status >= 300) {
        snackbarRef.current(
          getErrorMessage(response, "Failed to fetch brands"),
          "error"
        );
        setBrands([]);
        return;
      }

      const json = response?.data;
      const rows = Array.isArray(json?.brands)
        ? json.brands
        : Array.isArray(json?.Brands)
          ? json.Brands
          : Array.isArray(json?.data)
            ? json.data
            : Array.isArray(json?.Data)
              ? json.Data
              : Array.isArray(json)
                ? json
                : [];
      setBrands(rows.map(normalizeBrand));
    } catch (error) {
      console.error("Error fetching brands:", error);
      snackbarRef.current("Failed to fetch brands", "error");
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const refreshBrandDependencies = useCallback(async () => {
    await Promise.all([
      fetchBrands(),
      onRefreshDomains?.(),
      onRefreshAliases?.(),
    ]);
  }, [fetchBrands, onRefreshAliases, onRefreshDomains]);

  useEffect(() => {
    return () => {
      if (brandSearchTimerRef.current) {
        clearTimeout(brandSearchTimerRef.current);
        brandSearchTimerRef.current = null;
      }

      if (transferCompanySearchTimerRef.current) {
        clearTimeout(transferCompanySearchTimerRef.current);
        transferCompanySearchTimerRef.current = null;
      }
    };
  }, []);

  const selectedBrandOption = selectedUpsertBrand
    ? {
      value: selectedUpsertBrand.id,
      label: selectedUpsertBrand.brandName,
      brand: selectedUpsertBrand,
    }
    : null;

  const selectedAliasId = selectedAliasOption?.value || null;

  const handleBrandSearchInputChange = useCallback(
    (inputValue, actionMeta) => {
      if (actionMeta?.action !== "input-change") {
        return inputValue;
      }

      setBrandSearchInput(inputValue);
      setNewBrandName(inputValue);
      setSelectedUpsertBrand(null);
      setIsAliasConversion(false);

      if (brandSearchTimerRef.current) {
        clearTimeout(brandSearchTimerRef.current);
      }

      if (!isSystemAdmin || inputValue.trim().length < 2) {
        setBrandSearchOptions([]);
        setBrandSearchLoading(false);
        return inputValue;
      }

      setBrandSearchLoading(true);
      brandSearchTimerRef.current = setTimeout(async () => {
        try {
          const brandsResult = await fetchBrandOptions(inputValue.trim());
          setBrandSearchOptions(
            brandsResult.map((brand) => ({
              value: brand.id,
              label: brand.brandName,
              brand,
            }))
          );
        } catch (error) {
          console.error("Failed to search brands", error);
          setBrandSearchOptions([]);
        } finally {
          setBrandSearchLoading(false);
        }
      }, BRAND_SEARCH_DEBOUNCE_MS);

      return inputValue;
    },
    [isSystemAdmin]
  );

  const handleBrandSelectChange = useCallback((option) => {
    if (!option) {
      setSelectedUpsertBrand(null);
      setNewBrandName("");
      setBrandSearchInput("");
      setNewBrandWebsite("");
      setNewBrandStatus(DEFAULT_STATUS);
      setNewBrandLogo(null);
      setIsAliasConversion(false);
      return;
    }

    const selectedBrand = option.brand;
    if (selectedBrand) {
      setSelectedUpsertBrand(selectedBrand);
      setNewBrandName(selectedBrand.brandName || "");
      setNewBrandStatus(normalizeStatusValue(selectedBrand.status));
      setNewBrandWebsite(selectedBrand.website || "");
      setNewBrandLogo(null);
      setNewBrandIsDefault(!!selectedBrand.isDefault);
      setBrandSearchInput("");
      setIsAliasConversion(false);
      return;
    }

    const brandName = option.label || option.value || "";
    setSelectedUpsertBrand(null);
    setNewBrandName(brandName);
    setBrandSearchInput("");
    setIsAliasConversion(false);
  }, []);

  const handleCreateBrandOption = useCallback((inputValue) => {
    setSelectedUpsertBrand(null);
    setNewBrandName(inputValue || "");
    setBrandSearchInput("");
    setBrandSearchOptions([]);
    setIsAliasConversion(false);
  }, []);

  const showBrandNameValidationSnackbar = (response, fallback) => {
    if (hasBrandNameConflict(response)) {
      snackbar(
        renderBrandValidationMessage(response, fallback),
        "error",
        undefined,
        10000
      );
      return true;
    }

    return false;
  };

  const showValidationSnackbar = (response, fallback) => {
    const conflicts = getResponseConflicts(response);
    if (conflicts.length > 0) {
      setValidationConflictModal({
        show: true,
        conflicts: conflicts.map(normalizeBrandValidationConflict),
      });
      return true;
    }

    return false;
  };

  const closeValidationConflictModal = () => {
    setValidationConflictModal({
      show: false,
      conflicts: [],
    });
  };

  const openDuplicateDomainModal = (response, website, source) => {
    const companies = getDuplicateDomainConflicts(response, website);
    if (companies.length === 0) {
      return false;
    }

    setDuplicateDomainModal({
      show: true,
      website: website || companies[0]?.domain || "",
      companies,
      source,
    });
    setSelectedDuplicateCompanyIds({});
    return true;
  };

  const closeDuplicateDomainModal = () => {
    setDuplicateDomainModal({
      show: false,
      website: "",
      companies: [],
      source: null,
    });
    setSelectedDuplicateCompanyIds({});
  };

  const getCompanyDomainId = async (targetCompanyId, website) => {
    const normalizedWebsite = (website || "").trim().toLowerCase();
    if (!targetCompanyId || !normalizedWebsite) {
      return "";
    }

    const response = await rget(
      `${DEV_URL}/api/company/GetCompanyDomains/${targetCompanyId}`
    );
    const domains = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data?.Data)
        ? response.data.Data
        : [];
    const match = domains.find(
      (item) =>
        (item?.domain || item?.Domain || "").trim().toLowerCase() ===
        normalizedWebsite
    );

    return match?.id || match?.Id || "";
  };

  const openAddModal = () => {
    setNewBrandName("");
    setNewBrandStatus(DEFAULT_STATUS);
    setNewBrandWebsite("");
    setNewBrandLogo(null);
    setNewBrandIsDefault(brands.length === 0);
    setSelectedAliasOption(null);
    setIsAliasConversion(false);
    setSelectedUpsertBrand(null);
    setBrandSearchInput("");
    setBrandSearchOptions([]);
    setBrandSearchLoading(false);
    setShowAddBrandModal(true);
  };

  const closeAddModal = () => {
    if (brandSearchTimerRef.current) {
      clearTimeout(brandSearchTimerRef.current);
      brandSearchTimerRef.current = null;
    }

    setShowAddBrandModal(false);
    setNewBrandLogo(null);
    setNewBrandIsDefault(false);
    setSelectedAliasOption(null);
    setIsAliasConversion(false);
    setSelectedUpsertBrand(null);
    setBrandSearchInput("");
    setBrandSearchOptions([]);
    setBrandSearchLoading(false);
  };

  useEffect(() => {
    if (!prefillAlias) return;

    const aliasId =
      prefillAlias.id ||
      prefillAlias.Id ||
      prefillAlias.aliasId ||
      prefillAlias.AliasId;
    const aliasName = prefillAlias.aliasName || prefillAlias.AliasName || "";
    const aliasDomain =
      prefillAlias.companyDomain ||
      prefillAlias.CompanyDomain ||
      prefillAlias.domain ||
      prefillAlias.Domain ||
      "";

    setSelectedAliasOption(
      aliasId && aliasName
        ? {
          value: aliasId,
          label: aliasName,
          alias: prefillAlias,
        }
        : null
    );
    setSelectedUpsertBrand(null);
    setNewBrandName(aliasName);
    setBrandSearchInput("");
    setBrandSearchOptions([]);
    setNewBrandStatus(DEFAULT_STATUS);
    setNewBrandWebsite(aliasDomain);
    setNewBrandLogo(null);
    setNewBrandIsDefault(brands.length === 0);
    setIsAliasConversion(true);
    setShowAddBrandModal(true);

    if (onConvertHandled) {
      onConvertHandled();
    }
  }, [prefillAlias, brands.length, onConvertHandled]);

  const handleAddBrand = async () => {
    const brandName = (newBrandName || brandSearchInput || "").trim();
    if (!brandName) {
      snackbar("Please enter a brand name", "error");
      return;
    }

    if (!isValidDomainOnly(newBrandWebsite)) {
      snackbar("Website must contains the name of domain only", "error");
      return;
    }

    setAddingBrand(true);
    try {
      const response = await rpost(
        `${DEV_URL}/api/Brand/Upsert`,
        buildBrandFormData({
          brandName,
          status: newBrandStatus,
          website: newBrandWebsite,
          logo: newBrandLogo,
          companyId,
          brandId: selectedUpsertBrand?.id,
          isDefault: newBrandIsDefault,
          aliasId: selectedAliasId,
        })
      );

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        if (showBrandNameValidationSnackbar(response, "Failed to save brand")) {
          return;
        }
        if (openDuplicateDomainModal(response, newBrandWebsite, "add")) {
          return;
        }
        if (showValidationSnackbar(response, "Failed to save brand")) {
          return;
        }
        snackbar(getErrorMessage(response, "Failed to save brand"), "error");
        return;
      }

      snackbar(
        getErrorMessage(response, "Brand saved successfully"),
        "success"
      );
      closeAddModal();
      await refreshBrandDependencies();
    } catch (error) {
      console.error("Error saving brand:", error);
      if (showBrandNameValidationSnackbar(error?.response, "Failed to save brand")) {
        return;
      }
      if (openDuplicateDomainModal(error?.response, newBrandWebsite, "add")) {
        return;
      }
      if (showValidationSnackbar(error?.response, "Failed to save brand")) {
        return;
      }
      snackbar("An error occurred while saving brand", "error");
    } finally {
      setAddingBrand(false);
    }
  };

  const openEditModal = (row) => {
    setEditBrandName(row.brandName);
    setEditStatus(normalizeStatusValue(row.status));
    setEditWebsite(row.website || "");
    setEditLogo(null);
    setEditBrandIsDefault(row.isDefault === true);
    setEditModal({ show: true, row });
  };

  const closeEditModal = () => {
    setEditModal({ show: false, row: null });
    setEditBrandName("");
    setEditStatus(DEFAULT_STATUS);
    setEditWebsite("");
    setEditLogo(null);
    setEditBrandIsDefault(false);
  };

  const handleSaveEdit = async () => {
    const row = editModal.row;
    if (!row?.id || !editBrandName.trim()) return;

    if (!isValidDomainOnly(editWebsite)) {
      snackbar("Website must contains the name of domain only", "error");
      return;
    }

    setSavingBrand(true);
    try {
      const response = await rput(
        `${DEV_URL}/api/Brand/${row.id}`,
        buildBrandFormData({
          brandName: editBrandName,
          status: editStatus,
          website: editWebsite,
          logo: editLogo,
          companyId: row.companyId || companyId,
          isDefault: editBrandIsDefault,
        })
      );

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        if (showBrandNameValidationSnackbar(response, "Failed to update brand")) {
          return;
        }
        if (openDuplicateDomainModal(response, editWebsite, "edit")) {
          return;
        }
        if (showValidationSnackbar(response, "Failed to update brand")) {
          return;
        }
        snackbar(getErrorMessage(response, "Failed to update brand"), "error");
        return;
      }

      snackbar("Brand updated successfully", "success");
      closeEditModal();
      await refreshBrandDependencies();
    } catch (error) {
      console.error("Error updating brand:", error);
      if (showBrandNameValidationSnackbar(error?.response, "Failed to update brand")) {
        return;
      }
      if (openDuplicateDomainModal(error?.response, editWebsite, "edit")) {
        return;
      }
      if (showValidationSnackbar(error?.response, "Failed to update brand")) {
        return;
      }
      snackbar("Failed to update brand", "error");
    } finally {
      setSavingBrand(false);
    }
  };

  const handleDuplicateDomainConfirm = async () => {
    const selectedCompanies = duplicateDomainModal.companies.filter(
      (item) => selectedDuplicateCompanyIds[item.companyId]
    );

    if (selectedCompanies.length === 0) {
      snackbar("Please select at least one company", "error");
      return;
    }

    try {
      setResolvingDuplicateDomain(true);

      for (const item of selectedCompanies) {
        const domainId = await getCompanyDomainId(
          item.companyId,
          item.domain || duplicateDomainModal.website
        );

        if (!domainId) {
          snackbar(
            `Unable to find matching domain for ${item.companyName}`,
            "error"
          );
          return;
        }

        const response = await rpost(
          `${DEV_URL}/api/company/${item.companyId}/Domain/${domainId}`,
          {},
          { headers: { "Content-Type": "application/json" } }
        );

        if (
          !response ||
          response.status < 200 ||
          response.status >= 300 ||
          response?.data?.success === false ||
          response?.data?.Success === false
        ) {
          snackbar(
            response?.data?.message ||
            `Failed to remove domain from ${item.companyName}`,
            "error"
          );
          return;
        }
      }

      const retrySource = duplicateDomainModal.source;
      closeDuplicateDomainModal();
      snackbar(
        selectedCompanies.length === 1
          ? "Domain removed successfully"
          : "Domains removed successfully",
        "success"
      );
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (retrySource === "edit") {
        await handleSaveEdit();
      } else {
        await handleAddBrand();
      }
    } catch (error) {
      console.error("Error resolving duplicate brand domain:", error);
      snackbar("An error occurred while resolving duplicate domain", "error");
    } finally {
      setResolvingDuplicateDomain(false);
    }
  };

  const openDeleteModal = (row) => setDeleteModal({ show: true, row });
  const closeDeleteModal = () => setDeleteModal({ show: false, row: null });

  const getHasBrandApplicationMapping = async (brandId) => {
    const response = await rpost(
      `${DEV_URL}/api/company/AssetApplicationMapping`,
      {
        AssetId: brandId,
        AssetType: "Brand",
      }
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify brand asset mapping",
        "error"
      );
      return null;
    }

    const resultData =
      response?.data?.data ?? response?.data?.Data ?? response?.data;
    const mappingResult = Array.isArray(resultData)
      ? resultData[0]
      : resultData;
    const hasApplicationMapping =
      mappingResult?.hasApplicationMapping ??
      mappingResult?.HasApplicationMapping ??
      false;

    return (
      hasApplicationMapping === true ||
      hasApplicationMapping === 1 ||
      String(hasApplicationMapping).toLowerCase() === "true"
    );
  };

  const handleRemoveBrandRequest = async (row) => {
    if (!row?.id) {
      snackbar("Unable to remove brand", "error");
      return;
    }

    try {
      setCheckingBrandMappingId(row.id);
      const hasApplicationMapping = await getHasBrandApplicationMapping(row.id);

      if (hasApplicationMapping === null) {
        return;
      }

      if (hasApplicationMapping) {
        openTransferModal(row, true);
        return;
      }

      openDeleteModal(row);
    } catch (error) {
      console.error("Error checking brand asset mapping:", error);
      snackbar("An error occurred while checking brand asset mapping", "error");
    } finally {
      setCheckingBrandMappingId(null);
    }
  };

  const handleDeleteBrand = async () => {
    const row = deleteModal.row;
    if (!row?.id) return;

    setDeletingBrand(true);
    try {
      const response = await rdelete(`${DEV_URL}/api/Brand/${row.id}`);
      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        snackbar(getErrorMessage(response, "Failed to remove brand"), "error");
        return;
      }

      closeDeleteModal();
      await refreshBrandDependencies();
      snackbar("Brand removed successfully", "success");
    } catch (error) {
      console.error("Error removing brand:", error);
      snackbar("Failed to remove brand", "error");
    } finally {
      setDeletingBrand(false);
    }
  };

  const openTransferModal = (row, fromMappedRemove = false) => {
    setSelectedTransferCompany(null);
    setTransferCompanyOptions([]);
    setTransferError("");
    setTransferModal({ show: true, row, fromMappedRemove });
    loadInitialTransferCompanies();
  };

  const closeTransferModal = () => {
    if (transferCompanySearchTimerRef.current) {
      clearTimeout(transferCompanySearchTimerRef.current);
      transferCompanySearchTimerRef.current = null;
    }
    setTransferModal({ show: false, row: null, fromMappedRemove: false });
    setSelectedTransferCompany(null);
    setTransferCompanyOptions([]);
    setTransferCompanyLoading(false);
    setTransferError("");
  };

  const filterTransferCompanies = useCallback(
    (options) => options.filter((option) => option.value !== companyId),
    [companyId]
  );

  const loadInitialTransferCompanies = useCallback(async () => {
    setTransferCompanyLoading(true);
    try {
      const options = await fetchCompanyPage("", 1);
      transferCompanyPageRef.current = 1;
      transferCompanySearchRef.current = "";
      transferCompanyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
      setTransferCompanyOptions(filterTransferCompanies(options));
    } catch (error) {
      console.error("Failed to fetch companies for brand transfer", error);
      setTransferCompanyOptions([]);
    } finally {
      setTransferCompanyLoading(false);
    }
  }, [filterTransferCompanies]);

  const handleTransferCompanyInputChange = useCallback(
    (inputValue, actionMeta) => {
      if (actionMeta?.action !== "input-change") {
        return inputValue;
      }

      if (transferCompanySearchTimerRef.current) {
        clearTimeout(transferCompanySearchTimerRef.current);
      }

      transferCompanySearchTimerRef.current = setTimeout(async () => {
        transferCompanySearchRef.current = inputValue;
        transferCompanyPageRef.current = 1;
        transferCompanyHasMoreRef.current = true;
        setTransferCompanyLoading(true);

        try {
          const options = await fetchCompanyPage(inputValue, 1);
          transferCompanyHasMoreRef.current =
            options.length >= COMPANY_PAGE_SIZE;
          setTransferCompanyOptions(filterTransferCompanies(options));
        } catch (error) {
          console.error("Failed to search companies for brand transfer", error);
          setTransferCompanyOptions([]);
        } finally {
          setTransferCompanyLoading(false);
        }
      }, 400);

      return inputValue;
    },
    [filterTransferCompanies]
  );

  const handleTransferCompanyScrollToBottom = useCallback(async () => {
    if (!transferCompanyHasMoreRef.current || transferCompanyLoading) {
      return;
    }

    setTransferCompanyLoading(true);
    try {
      const nextPage = transferCompanyPageRef.current + 1;
      const options = await fetchCompanyPage(
        transferCompanySearchRef.current,
        nextPage
      );
      transferCompanyPageRef.current = nextPage;
      transferCompanyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
      const filteredOptions = filterTransferCompanies(options);

      setTransferCompanyOptions((previousOptions) => {
        const existingValues = new Set(
          previousOptions.map((option) => option.value)
        );
        return [
          ...previousOptions,
          ...filteredOptions.filter(
            (option) => !existingValues.has(option.value)
          ),
        ];
      });
    } catch (error) {
      console.error("Failed to load more companies for brand transfer", error);
    } finally {
      setTransferCompanyLoading(false);
    }
  }, [transferCompanyLoading, filterTransferCompanies]);

  const handleTransferBrand = async () => {
    const row = transferModal.row;
    if (!row?.id || !selectedTransferCompany?.value) {
      setTransferError("Please select target company");
      return;
    }

    setTransferringBrand(true);
    setTransferError("");
    try {
      const response = await rpost(`${DEV_URL}/api/Brand/${row.id}/Transfer`, {
        SourceCompanyId: row.companyId || companyId,
        TargetCompanyId: selectedTransferCompany.value,
        Status: normalizeStatusValue(row.status),
      });

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        setTransferError(
          getErrorMessage(response, "Failed to transfer brand")
        );
        return;
      }

      snackbar("Brand transferred successfully", "success");
      closeTransferModal();
      await refreshBrandDependencies();
    } catch (error) {
      console.error("Error transferring brand:", error);
      setTransferError("Failed to transfer brand");
    } finally {
      setTransferringBrand(false);
    }
  };

  const columns = [
    {
      id: "logo",
      header: "",
      cell: ({ row }) => (
        <BrandRowLogo
          website={row.original.website}
          logo={row.original.logo}
          brandName={row.original.brandName}
        />
      ),
      enableSorting: false,
    },
    {
      header: "Brand",
      accessorKey: "brandName",
      cell: ({ row }) => (
        <span className="d-inline-flex align-items-center gap-2">
          <span>{row.original.brandName}</span>
          {row.original.isDefault ? (
            <span className="badge bg-primary" style={{ fontSize: 9 }}>
              Default
            </span>
          ) : null}
        </span>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => <BrandStatusIndicator status={row.original.status} />,
    },
    {
      header: "Website",
      accessorKey: "website",
      cell: ({ row }) =>
        row.original.website ? (
          <a href={getWebsiteUrl(row.original.website)} target="_blank" rel="noreferrer">
            {row.original.website}
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
  ];

  if (isSystemAdmin) {
    columns.push({
      id: "actions",
      header: "Action",
      cell: ({ row }) => (
        <ThreeDotMenu className="text-center">
          <DropdownItem onClick={() => openEditModal(row.original)}>
            <small>Edit Brand</small>
          </DropdownItem>
          <DropdownItem onClick={() => openTransferModal(row.original)}>
            <small>Transfer Brand</small>
          </DropdownItem>
          <DropdownItem
            disabled={checkingBrandMappingId === row.original.id}
            onClick={() => handleRemoveBrandRequest(row.original)}
          >
            <small>
              {checkingBrandMappingId === row.original.id ? "Checking..." : "Remove"}
            </small>
          </DropdownItem>
        </ThreeDotMenu>
      ),
      enableSorting: false,
      enableHiding: false,
    });
  }

  return (
    <span>
      <ConfirmationModal
        dialogClassName="modal-md"
        show={validationConflictModal.show}
        onHide={closeValidationConflictModal}
        centered
        title="Duplicate Brand / Domain"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        onConfirm={closeValidationConflictModal}
      >
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Associated Company</th>
                <th>Brand</th>
              </tr>
            </thead>
            <tbody>
              {validationConflictModal.conflicts.map((item, index) => (
                <tr key={`${item.companyId || "company"}-${index}`}>
                  <td>
                    {item.companyId ? (
                      <a
                        href={`/companies/${item.companyId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.companyName}
                      </a>
                    ) : (
                      item.companyName
                    )}
                  </td>
                  <td>{item.brandName || item.matchedValue || item.domain || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ConfirmationModal>

      <Div className="mt-4 d-flex justify-content-between align-items-center">
        <PageTitle title="Brands"></PageTitle>
        <Div className="d-flex align-items-center gap-2">
          {isSystemAdmin && (
            <Button
              variant="primary"
              size="sm"
              className="fs-10"
              style={{
                padding: "2px 6px",
                fontSize: "10px",
                lineHeight: "1.2",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
              }}
              onClick={openAddModal}
            >
              <PlusIcon style={{ fontSize: "9px" }} />
              <small>Add Brand</small>
            </Button>
          )}
        </Div>
      </Div>

      <Card>
        <Card.Body>
          <Row>
            <Col sm="12">
              <Table
                  columns={columns}
                  data={brands}
                  loading={loading}
                  enableSearch={false}
                  enablePagination={false}
                  enableHideColumns={false}
                  enableSorting
                  enableFilters={false}
                  enableSelection={false}
                  enablePaginateAll={false}
                  enableColumnVisibilityStorage={false}
                  enableGlobalFilterStorage={false}
                  enableUrlPagination={false}
                  noDataMessage="No brands found"
                />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <ConfirmationModal
        dialogClassName="modal-md"
        show={duplicateDomainModal.show}
        onHide={closeDuplicateDomainModal}
        centered
        title="Duplicate Domain"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={resolvingDuplicateDomain}
        onConfirm={handleDuplicateDomainConfirm}
      >
        <p className="mb-3">
          <strong>{duplicateDomainModal.website}</strong> is already associated
          with the following companies. Select the company associations to
          remove before saving this brand.
        </p>
        <div className="d-flex flex-column gap-2">
          {duplicateDomainModal.companies.map((item) => (
            <div key={item.companyId} className="border rounded p-2">
              <Form.Check
                id={`brand-duplicate-domain-${item.companyId}`}
                checked={Boolean(selectedDuplicateCompanyIds[item.companyId])}
                onChange={(event) =>
                  setSelectedDuplicateCompanyIds((current) => ({
                    ...current,
                    [item.companyId]: event.target.checked,
                  }))
                }
                label={
                  <span className="d-flex flex-column">
                    <a
                      href={`/companies/${item.companyId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.companyName}
                    </a>
                    {item.companyStatus ? (
                      <small className="text-muted">{item.companyStatus}</small>
                    ) : null}
                  </span>
                }
              />
            </div>
          ))}
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        show={showAddBrandModal}
        onHide={closeAddModal}
        centered
        title={isAliasConversion ? "Convert to Brand" : "Add Brand"}
        confirmButtonLabel={addingBrand ? "Saving..." : "Save Brand"}
        confirmButtonVariant="primary"
        isLoading={addingBrand}
        onConfirm={() => {
          if (!(newBrandName || brandSearchInput).trim()) return;
          handleAddBrand();
        }}
      >
          <Form.Group className="mb-3">
            <Form.Label>Brand Name</Form.Label>
            {isAliasConversion ? (
              <FormInput
                type="text"
                containerClass="mb-0"
                value={newBrandName}
                readOnly
              />
            ) : isSystemAdmin ? (
              <CreatableSelect
                isClearable
                placeholder="Search or enter brand name"
                value={selectedBrandOption}
                inputValue={brandSearchInput}
                options={brandSearchOptions}
                isLoading={brandSearchLoading}
                onInputChange={handleBrandSearchInputChange}
                onChange={handleBrandSelectChange}
                onCreateOption={handleCreateBrandOption}
                isValidNewOption={() => false}
                noOptionsMessage={() =>
                  brandSearchInput.trim().length < 2
                    ? "Type at least 2 characters"
                    : "No brands found"
                }
                styles={companySelectStyles}
                menuPortalTarget={document.body}
              />
            ) : (
              <FormInput
                type="text"
                containerClass="mb-0"
                placeholder="Enter brand name"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
              />
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Website</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <FormInput
                type="text"
                containerClass="flex-grow-1 mb-0"
                placeholder="example.com"
                value={newBrandWebsite}
                onChange={(e) => setNewBrandWebsite(e.target.value)}
              />
              <WebsiteLogoPreview
                logoUrl={newBrandWebsiteLogoPreview}
                fallbackLogoUrl={
                  newBrandLogoFallbackPreview || selectedUpsertBrand?.logo || ""
                }
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Logo</Form.Label>
            <FileUploader
              value={newBrandLogo ? [newBrandLogo] : []}
              accept={LOGO_FILE_ACCEPT}
              onChange={(files) => setNewBrandLogo(files[0] || null)}
              onRejected={() => snackbar(LOGO_FILE_ERROR, "error")}
              multiple={false}
              disabled={addingBrand}
              customPlaceholder={<h5>Drop a logo here or click to upload.</h5>}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Select
              options={STATUS_OPTIONS}
              value={findOptionByValue(STATUS_OPTIONS, newBrandStatus)}
              onChange={(option) =>
                setNewBrandStatus(option?.value ?? DEFAULT_STATUS)
              }
              isSearchable={false}
              isClearable={false}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Set as Default</Form.Label>
            <Select
              options={DEFAULT_OPTIONS}
              value={findOptionByValue(
                DEFAULT_OPTIONS,
                newBrandIsDefault ? "true" : "false"
              )}
              onChange={(option) =>
                setNewBrandIsDefault(option?.value === "true")
              }
              isSearchable={false}
              isClearable={false}
            />
          </Form.Group>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        show={editModal.show}
        onHide={closeEditModal}
        centered
        title="Edit Brand"
        confirmButtonLabel={savingBrand ? "Saving..." : "Save"}
        confirmButtonVariant="primary"
        isLoading={savingBrand}
        onConfirm={() => {
          if (!editBrandName.trim()) return;
          handleSaveEdit();
        }}
      >
          <FormInput
            type="text"
            label="Brand Name"
            containerClass="mb-3"
            value={editBrandName}
            onChange={(e) => setEditBrandName(e.target.value)}
            placeholder="Enter brand name"
          />
          <Form.Group className="mb-3">
            <Form.Label>Website</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <FormInput
                type="text"
                containerClass="flex-grow-1 mb-0"
                value={editWebsite}
                onChange={(e) => setEditWebsite(e.target.value)}
                placeholder="example.com"
              />
              <WebsiteLogoPreview
                logoUrl={editWebsiteLogoPreview}
                fallbackLogoUrl={
                  editLogoFallbackPreview || editModal.row?.logo || ""
                }
              />
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Logo</Form.Label>
            {editModal.row?.logo && (
              <div className="mb-2">
                <img
                  src={editModal.row.logo}
                  alt={`${editModal.row.brandName} logo`}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "contain",
                    border: "1px solid var(--ct-border-color)",
                    borderRadius: 4,
                    padding: 4,
                  }}
                />
              </div>
            )}
            <FileUploader
              value={editLogo ? [editLogo] : []}
              accept={LOGO_FILE_ACCEPT}
              onChange={(files) => setEditLogo(files[0] || null)}
              onRejected={() => snackbar(LOGO_FILE_ERROR, "error")}
              multiple={false}
              disabled={savingBrand}
              customPlaceholder={<h5>Drop a logo here or click to upload.</h5>}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Select
              options={STATUS_OPTIONS}
              value={findOptionByValue(STATUS_OPTIONS, editStatus)}
              onChange={(option) =>
                setEditStatus(option?.value ?? DEFAULT_STATUS)
              }
              isSearchable={false}
              isClearable={false}
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Set as Default</Form.Label>
            <Select
              options={DEFAULT_OPTIONS}
              value={findOptionByValue(
                DEFAULT_OPTIONS,
                editBrandIsDefault ? "true" : "false"
              )}
              onChange={(option) =>
                setEditBrandIsDefault(option?.value === "true")
              }
              isSearchable={false}
              isClearable={false}
            />
          </Form.Group>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        show={deleteModal.show}
        onHide={closeDeleteModal}
        centered
        title="Remove Brand"
        confirmButtonLabel="Remove"
        confirmButtonVariant="danger"
        isLoading={deletingBrand}
        onConfirm={handleDeleteBrand}
      >
        <p>
          Are you sure you want to remove{" "}
          <strong>{deleteModal.row?.brandName}</strong>?
        </p>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        show={transferModal.show}
        onHide={closeTransferModal}
        centered
        title="Transfer Brand"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={transferringBrand}
        onConfirm={handleTransferBrand}
      >
        {transferError ? <Alert variant="danger">{transferError}</Alert> : null}
        {transferModal.fromMappedRemove ? (
          <p>
            This brand is used in another product for the same company, so you
            cannot remove it. You can transfer it to another company, or first
            delete/correct the product data, and then remove it.
          </p>
        ) : (
          <p>
            Transfer brand <strong>{transferModal.row?.brandName}</strong> to a
            different company.
          </p>
        )}
        <Form.Group>
          <Form.Label>Select Target Company</Form.Label>
          <Select
            options={transferCompanyOptions}
            onMenuOpen={() => {
              if (transferCompanyOptions.length === 0) {
                loadInitialTransferCompanies();
              }
            }}
            onMenuScrollToBottom={handleTransferCompanyScrollToBottom}
            onInputChange={handleTransferCompanyInputChange}
            value={selectedTransferCompany}
            onChange={(value) => {
              setSelectedTransferCompany(value);
              setTransferError("");
            }}
            placeholder="Search company..."
            isClearable
            isSearchable
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            isLoading={transferCompanyLoading}
            maxMenuHeight={250}
          />
        </Form.Group>
      </ConfirmationModal>
    </span>
  );
};
